import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export interface KanbanSelectOption {
  value: string;
  label: string;
}

interface KanbanSelectProps {
  value: string;
  options: KanbanSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  title?: string;
}

const MENU_MAX = 240;
const MENU_GAP = 6;

export default function KanbanSelect({
  value,
  options,
  onChange,
  placeholder = 'Selecione…',
  className = '',
  disabled = false,
  title,
}: KanbanSelectProps) {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState({ top: 0, left: 0, width: 0, maxHeight: MENU_MAX });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);
  const label = selected?.label || placeholder;

  const placeMenu = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom - MENU_GAP - 8;
    const spaceAbove = r.top - MENU_GAP - 8;
    const openUp = spaceBelow < 120 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(MENU_MAX, Math.max(80, openUp ? spaceAbove : spaceBelow));
    setMenu({
      top: openUp ? r.top - MENU_GAP - maxHeight : r.bottom + MENU_GAP,
      left: r.left,
      width: Math.max(r.width, 160),
      maxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    placeMenu();
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onMove = () => placeMenu();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  return (
    <div className={`kanban-select ${className}`.trim()}>
      <button
        type="button"
        ref={triggerRef}
        className="kanban-select-trigger"
        disabled={disabled}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { if (!disabled) setOpen(v => !v); }}
      >
        <span className={selected ? '' : 'kanban-select-placeholder'}>{label}</span>
        <ChevronDown size={14} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="kanban-select-menu"
          role="listbox"
          style={{
            top: menu.top,
            left: menu.left,
            width: menu.width,
            maxHeight: menu.maxHeight,
          }}
        >
          {options.length === 0 ? (
            <div className="kanban-select-empty">Nenhuma opção</div>
          ) : options.map(opt => (
            <button
              type="button"
              key={opt.value || '__empty'}
              role="option"
              aria-selected={opt.value === value}
              className={`kanban-select-option ${opt.value === value ? 'active' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
